import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export type Comment = {
  id: string;
  post_id: string;
  user_id: string;
  text_content: string;
  created_at: string;
  profile_username?: string;
  profile_emoji?: string;
};

function useComments(table: 'community_post_comments' | 'gallery_post_comments', postId: string | null) {
  return useQuery({
    queryKey: [table, postId],
    queryFn: async () => {
      if (!postId) return [];
      const { data, error } = await supabase
        .from(table)
        .select('*')
        .eq('post_id', postId)
        .order('created_at', { ascending: true })
        .limit(50);
      if (error) throw error;
      if (!data?.length) return [];

      const userIds = [...new Set(data.map(c => c.user_id))];
      const { data: profiles } = await supabase.from('profiles').select('user_id, username, avatar_emoji').in('user_id', userIds);
      const profileMap = new Map((profiles || []).map(p => [p.user_id, p]));

      return data.map(c => ({
        ...c,
        profile_username: profileMap.get(c.user_id)?.username || 'Unknown',
        profile_emoji: profileMap.get(c.user_id)?.avatar_emoji || '🧙',
      })) as Comment[];
    },
    enabled: !!postId,
  });
}

function useAddComment(table: 'community_post_comments' | 'gallery_post_comments') {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ postId, text }: { postId: string; text: string }) => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase.from(table).insert({
        post_id: postId,
        user_id: user.id,
        text_content: text,
      });
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: [table, vars.postId] });
    },
  });
}

export function useCommunityComments(postId: string | null) {
  return useComments('community_post_comments', postId);
}
export function useAddCommunityComment() {
  return useAddComment('community_post_comments');
}
export function useGalleryComments(postId: string | null) {
  return useComments('gallery_post_comments', postId);
}
export function useAddGalleryComment() {
  return useAddComment('gallery_post_comments');
}
